import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AGGridHelix } from '@lwt-helix/ag-grid';
import { defaultColumnDef } from './colSettings';
import { generateTableColDefs, handleDrawDivider, handleSortedHighlight } from './agGrid';
import ColumnHeader from './ColumnHeader';
import { tableViewNames } from '../../../../agent-production/constants/agentProductionConstants';
import { agentProductionTerms as apt } from '../../../../constants';
import { status } from '../../../../constants';
import { cssClassNames } from '../../../../constants/css';
import { getColumnDividerObserver } from './columnDividers';
import { applyPersistedSelection, getRowIdParam } from '../../../helpers/agGrid';
import { useSearchStore } from '../../../../store/store';
import { retainTerms as rt } from './retainedSettings';
import { getIsCrossSortEnabled } from './crossSort';
import { getIsCrossViewSelectionEnabled, getSelectedAgentIds } from './crossViewSelection';

const Table = (props) => {
    const {
        tableView,
        rowData,
        pinnedBottomRowData,
        columns,
        additionalTableHeaders,
        tableTitleWithTooltips,
        externalParams,
        rowSelection,
        onCellClicked,
        gridRef,
        onRowClicked,
        suppressNoRowsOverlay,
        updateDisplayedOffices,
        isTableAutoHeight=true,
        hasFooter,
        suppressRowClickSelection = false,
        onSelectionChanged,
        rowMultiSelectWithClick = false
    } = props;

    const {
        restoreTableRetainedStateByType,
        setTableRetainedStateByType,
        handleFloatingFilterRetain,
        setSelectedRowIndex,
        setCrossSortOrder,
        crossSortOrder,
        crossSortSourceTableId,
        crossSortColId,
        crossSortDirection,
        selectedModule,
        selectedAgentIds,
        setSelectedAgentIds,
    } = useSearchStore(state => ({
        restoreTableRetainedStateByType: state.restoreTableRetainedStateByType,
        setTableRetainedStateByType: state.setTableRetainedStateByType,
        handleFloatingFilterRetain: state.handleFloatingFilterRetain,
        setSelectedRowIndex: state.setSelectedRowIndex,
        setCrossSortOrder: state.setCrossSortOrder,
        crossSortOrder: state[state.selectedModule]?.crossSortOrder,
        crossSortSourceTableId: state[state.selectedModule]?.crossSortSourceTableId,
        crossSortColId: state[state.selectedModule]?.crossSortColId,
        crossSortDirection: state[state.selectedModule]?.crossSortDirection,
        selectedModule: state.selectedModule,
        selectedAgentIds: getSelectedAgentIds(state),
        setSelectedAgentIds: state.setSelectedAgentIds,
    }));
    const isCrossSortEnabled = getIsCrossSortEnabled(selectedModule, tableView?.id);
    const isCrossViewSelectionEnabled = getIsCrossViewSelectionEnabled(selectedModule, tableView?.id);

    useEffect(() => {
        applyPersistedSelection(gridRef?.current?.api, selectedAgentIds, isCrossViewSelectionEnabled);
    }, [selectedAgentIds, isCrossViewSelectionEnabled, gridRef]);

    const [groupHeaders, setGroupHeaders] = useState(undefined);

    const agGridComponents = useMemo(() => {
        return {
            agColumnHeader: ColumnHeader
        };
    }, []);

    useEffect(() => {
        if (!isCrossSortEnabled) return;
        if (
            crossSortSourceTableId &&
            crossSortSourceTableId !== tableView?.id &&
            gridRef?.current?.api
        ) {
            gridRef.current.api.applyColumnState({
                defaultState: { sort: null }
            });
            gridRef.current.api.refreshHeader();
        }
    }, [crossSortOrder, crossSortSourceTableId, tableView?.id, isCrossSortEnabled, gridRef]);

    useEffect(() => {
        const tableObserver = getColumnDividerObserver(tableView?.id);
        if (tableObserver) {
            const tableContainer = document.querySelector(`#${tableView?.id}`);
            tableObserver.observe(tableContainer, { childList: true, subtree: true });
            return () => tableObserver.disconnect();
        }
    }, [tableView?.id]);

    // It generates table colDefs dynamically.
    const columnDefs = useMemo(() => {
        return generateTableColDefs({
            columns,
            tableView,
            additionalTableHeaders,
            tableTitleWithTooltips,
            externalParams: {...externalParams, tableId: tableView?.id},
        });
    }, [columns, tableView, additionalTableHeaders, tableTitleWithTooltips, externalParams]);

    // It validates if additionalTab1leHeaders were updated.
    useEffect(() => {
        switch (tableView?.id) {
            case tableViewNames.overview:
                if (additionalTableHeaders[0].column1.title.length > 33
                    && additionalTableHeaders[0].column2.title.length > 39
                    && (!groupHeaders || (groupHeaders !== additionalTableHeaders))) {
                    setGroupHeaders(additionalTableHeaders);
                }
                break;
            default:
                if (!groupHeaders || (groupHeaders !== additionalTableHeaders)) {
                    setGroupHeaders(additionalTableHeaders);
                }
                break;
        }
    }, [additionalTableHeaders]);

    useEffect(() => {
        if (!gridRef || !columns) return;
        if (gridRef.current?.api) {
            const api = gridRef.current.api;
            columns.forEach(col => {
                api.setColumnsVisible([col.id], col.isDisplayed);
            });
        }
    }, [columns, gridRef]);

    const shouldSetScrollIndex = [
        tableViewNames.availableAgents,
        tableViewNames.availableOffices,
        tableViewNames.addedAgents,
        tableViewNames.addedOffices,
    ].includes(tableView?.id);

    const handleRowClicked = (e) => {
        const isAvailableAgentsActive = tableView?.id === tableViewNames.availableAgents &&
            e.data.status === status.ACTIVE;
        setSelectedRowIndex(e.rowIndex);
        if (shouldSetScrollIndex && (tableView?.id !== tableViewNames.availableAgents || isAvailableAgentsActive)) {
            if(!onRowClicked) return;
            onRowClicked(e.data);
        }
    };

    const rowClassRules = useMemo(() => {
        return ({
            [cssClassNames.inactiveRow]: (params) => params.data[params.data?.statusKeyfield] === status.INACTIVE
        });
    }, []);

    const tablesWithNoHighlight = [tableViewNames.addedAgents, tableViewNames.availableAgents];

    const handleTableAction = (e, newSourceTableId = null) => {
        if (tablesWithNoHighlight.includes(tableView?.id)) return;
        const sourceTableId = newSourceTableId ?? crossSortSourceTableId;
        handleSortedHighlight(e, tableView, sourceTableId);
    };

    const handleSortChanged = e => {
        const columnState = e.api.getColumnState();
        const hasActiveSort = columnState.some(col => col.sort);

        if (!hasActiveSort) return;
        if (isCrossSortEnabled && e.source === 'uiColumnSorted') {
            const orderedData = [];
            e.api.forEachNodeAfterFilterAndSort(node => {
                if (node.data) orderedData.push(node.data);
            });
            const sortedColState = columnState.find(col => col.sort);
            setCrossSortOrder(orderedData, tableView?.id, sortedColState?.colId, sortedColState?.sort);
            handleTableAction(e, tableView?.id);
            return;
        }
        handleTableAction(e);
    };


    const handleGridReady = e => {
        if (isCrossSortEnabled &&
            tableView?.id === crossSortSourceTableId
            && crossSortColId && crossSortDirection) {
            e.api.applyColumnState({
                state: [{ colId: crossSortColId, sort: crossSortDirection }],
                defaultState: { sort: null }
            });
            handleTableAction(e, tableView?.id);
            restoreTableRetainedStateByType({ api: e.api, tableId: tableView?.id, type: rt.floatingFilter });
            return;
        }
        restoreTableRetainedStateByType({ api: e.api, tableId: tableView?.id, type: rt.sort });
        restoreTableRetainedStateByType({ api: e.api, tableId: tableView?.id, type: rt.floatingFilter });
        handleTableAction(e);
    };

    const handleFirstDataRendered = e => {
        applyPersistedSelection(e.api, selectedAgentIds, isCrossViewSelectionEnabled);
    };

    const handleOnFilterChanged = e => {
        if (updateDisplayedOffices) {
            updateDisplayedOffices(e);
        }
        handleFloatingFilterRetain({ tableViewId: tableView?.id, api: e.api, setTableRetainedStateByType});
    };

    const handleOnRowDataChanged = e => {
        handleTableAction(e);
        if (updateDisplayedOffices) {
            updateDisplayedOffices(e);
        }
        applyPersistedSelection(e.api, selectedAgentIds, isCrossViewSelectionEnabled);
    }

    const handleSelectionChanged = e => {
        if (isCrossViewSelectionEnabled && e.source !== 'api') {
            const selectedIds = e.api.getSelectedRows().map(row => row?.agentId);
            setSelectedAgentIds(selectedIds);
        }
        if (onSelectionChanged) onSelectionChanged(e);
    };
    const handleGridColumnsChange = e => {
        handleTableAction(e);
        handleDrawDivider(tableView?.id, columnDefs);
    };

    const gridOptions = {
        localeText: {
            noRowsToShow: apt.searchReturnedNoResults
        }
    };

    const tablesWithoutAutoHeight = [
        tableViewNames.addedAgents,
        tableViewNames.addedOffices,
        tableViewNames.availableAgents,
        tableViewNames.availableOffices,
        tableViewNames.overview,
        tableViewNames.activity,
        tableViewNames.contactInformation,
        tableViewNames.growthAnalysisLtm,
        tableViewNames.growthAnalysisYtd,
        tableViewNames.listingProficiency,
        tableViewNames.incDecGrowthAnalysisLtm,
        tableViewNames.growthAnalysisL90,
        tableViewNames.incDecOverview,
        tableViewNames.lostAgentsBreakdown,
        tableViewNames.agentsGainedBreakdown,
        tableViewNames.officeBreakdown,
        tableViewNames.agentDetailProduction,
        tableViewNames.incDecGrowthAnalysisYtd,
    ];

    const domLayout = (tablesWithoutAutoHeight.includes(tableView?.id) || !isTableAutoHeight) && !hasFooter ? 'normal' : 'autoHeight';
    const rowIdParam = getRowIdParam(tableView?.id);
    return (
        <div id={tableView?.id} className={`mt-2 ${rowData?.length === 0 ? 'ag-no-table-results' : ''}`}>
            <AGGridHelix
                key={tableView?.id}
                ref={gridRef}
                defaultColDef={defaultColumnDef}
                columnDefs={columnDefs}
                context={{tableViewId: tableView?.id}}
                rowData={rowData}
                pagination={tableView?.showPagination}
                paginationPageSize={tableView?.paginationOptions?.sizePerPage}
                sizePerPageList={tableView?.paginationOptions?.sizePerPageList}
                domLayout={domLayout}
                components={agGridComponents}
                rowSelection={rowSelection}
                onCellClicked={onCellClicked}
                onVirtualColumnsChanged={handleTableAction}
                onSortChanged={handleSortChanged}
                onColumnResized={handleGridColumnsChange}
                onRowDataChanged={handleOnRowDataChanged}
                suppressCsvExport={false}
                onGridColumnsChanged={handleGridColumnsChange}
                onBodyScroll={handleTableAction}
                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                onRowClicked={handleRowClicked}
                gridOptions={gridOptions}
                rowClassRules={rowClassRules}
                suppressNoRowsOverlay={suppressNoRowsOverlay}
                onFilterChanged={handleOnFilterChanged}
                pinnedBottomRowData={pinnedBottomRowData || []}
                {...rowIdParam}
                deltaRowDataMode={true}
                suppressRowClickSelection={suppressRowClickSelection}
                onSelectionChanged={handleSelectionChanged}
                rowMultiSelectWithClick={rowMultiSelectWithClick}
            />
        </div>
    );
};

Table.propTypes = {
    tableView: PropTypes.object.isRequired,
    rowData: PropTypes.array.isRequired,
    columns: PropTypes.array,
    additionalTableHeaders: PropTypes.array,
    tableTitleWithTooltips: PropTypes.object.isRequired,
    externalParams: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    rowSelection: PropTypes.string,
    onCellClicked: PropTypes.func,
    gridRef: PropTypes.object,
    updateDisplayedOffices: PropTypes.func,
    onRowClicked: PropTypes.func,
    suppressNoRowsOverlay: PropTypes.bool,
    pinnedBottomRowData: PropTypes.array,
    isTableAutoHeight: PropTypes.bool,
    hasFooter: PropTypes.bool,
    suppressRowClickSelection: PropTypes.bool,
    onSelectionChanged: PropTypes.func,
    rowMultiSelectWithClick: PropTypes.bool,
};

export default Table;
